import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { 
  Sparkles, FolderOpen, Copy, Play, ArrowRight, 
  Save, Clock, Share2
} from 'lucide-react';
import PublicHeader from '@/components/PublicHeader';

export default function TemplateManagementPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-slate-50">
      <PublicHeader breadcrumbs={[{ label: 'Features', link: '/how-it-works' }, { label: 'Template Management' }]} />

      <div className="pt-32">
      {/* Hero Section */}
      <section className="py-16 bg-gradient-to-br from-violet-600 to-indigo-600">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center text-white">
            <div className="w-20 h-20 rounded-2xl bg-white/10 backdrop-blur-sm flex items-center justify-center mx-auto mb-6">
              <Sparkles className="w-10 h-10" />
            </div>
            <h1 className="text-5xl font-bold mb-4">Template Management</h1>
            <p className="text-xl text-violet-100 max-w-3xl mx-auto">
              Save, organize, and reuse templates across your organization. Run templates unlimited times with different data.
            </p>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">How It Works</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Organize and reuse your templates efficiently
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Step 1 */}
            <div className="bg-white rounded-2xl border border-slate-200 p-8">
              <div className="w-12 h-12 rounded-xl bg-violet-100 text-violet-600 font-bold text-xl flex items-center justify-center mb-6">
                1
              </div>
              <div className="flex items-center gap-3 mb-4">
                <Save className="w-6 h-6 text-violet-600" />
                <h3 className="text-xl font-bold text-slate-900">Save Templates</h3>
              </div>
              <p className="text-slate-600 leading-relaxed">
                Once you've created a template, save it to your library. Add descriptions and categorize for easy retrieval.
              </p>
            </div>

            {/* Step 2 */}
            <div className="bg-white rounded-2xl border border-slate-200 p-8">
              <div className="w-12 h-12 rounded-xl bg-violet-100 text-violet-600 font-bold text-xl flex items-center justify-center mb-6">
                2
              </div>
              <div className="flex items-center gap-3 mb-4">
                <FolderOpen className="w-6 h-6 text-violet-600" />
                <h3 className="text-xl font-bold text-slate-900">Organize</h3>
              </div>
              <p className="text-slate-600 leading-relaxed">
                Use categories and tags to organize templates. Search and filter to find the right template instantly.
              </p>
            </div>

            {/* Step 3 */}
            <div className="bg-white rounded-2xl border border-slate-200 p-8">
              <div className="w-12 h-12 rounded-xl bg-violet-100 text-violet-600 font-bold text-xl flex items-center justify-center mb-6">
                3
              </div>
              <div className="flex items-center gap-3 mb-4">
                <Play className="w-6 h-6 text-violet-600" />
                <h3 className="text-xl font-bold text-slate-900">Reuse Unlimited</h3>
              </div>
              <p className="text-slate-600 leading-relaxed">
                Run the same template with different data sets. No limit on how many times you can use a template.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Powerful Features</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Everything you need to manage templates at scale
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-slate-50 rounded-xl p-6 border border-slate-200">
              <div className="w-12 h-12 rounded-lg bg-violet-100 flex items-center justify-center mb-4">
                <FolderOpen className="w-6 h-6 text-violet-600" />
              </div>
              <h3 className="font-bold text-slate-900 mb-2">Categories</h3>
              <p className="text-sm text-slate-600">Organize with custom categories</p>
            </div>

            <div className="bg-slate-50 rounded-xl p-6 border border-slate-200">
              <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center mb-4">
                <Copy className="w-6 h-6 text-blue-600" />
              </div>
              <h3 className="font-bold text-slate-900 mb-2">Duplicate</h3>
              <p className="text-sm text-slate-600">Clone templates to create variants</p>
            </div>

            <div className="bg-slate-50 rounded-xl p-6 border border-slate-200">
              <div className="w-12 h-12 rounded-lg bg-green-100 flex items-center justify-center mb-4">
                <Clock className="w-6 h-6 text-green-600" />
              </div>
              <h3 className="font-bold text-slate-900 mb-2">Version History</h3>
              <p className="text-sm text-slate-600">Track all template runs</p>
            </div>

            <div className="bg-slate-50 rounded-xl p-6 border border-slate-200">
              <div className="w-12 h-12 rounded-lg bg-orange-100 flex items-center justify-center mb-4">
                <Share2 className="w-6 h-6 text-orange-600" />
              </div>
              <h3 className="font-bold text-slate-900 mb-2">Team Sharing</h3>
              <p className="text-sm text-slate-600">Share across your organization</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold text-slate-900 mb-6">Build Your Template Library</h2>
          <p className="text-lg text-slate-600 mb-8">
            Start organizing and reusing your document templates efficiently
          </p>
          <Button
            onClick={() => navigate('/auth')}
            size="lg"
            className="h-14 px-10 rounded-full bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 text-white text-lg font-bold"
          >
            Get Started Free
            <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </div>
      </section>
      </div>
    </div>
  );
}
